import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const TYPE_LABEL = { rating: "0–10 scale", single: "Choice", integer: "Number", short_text: "Short text", long_text: "Long text" };
const TEXT_ONLY = ["short_text", "long_text"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30);

export function uniqueCode(label, taken) {
  const base = slug(label) || "question";
  let code = base;
  for (let n = 2; taken.has(code); n += 1) code = `${base}_${n}`;
  return code;
}

function buildField({ type, label, prompt, help, options, unit, better }, taken) {
  const f = { code: uniqueCode(label, taken), type, label: label.trim(), prompt: prompt.trim() };
  if (help.trim()) f.help = help.trim();
  if (type === "rating" && better !== "none") f.better = better;
  if (type === "integer") Object.assign(f, { min: 0 }, unit.trim() ? { unit: unit.trim() } : {}, better !== "none" ? { better } : {});
  if (type === "single") {
    const seen = new Set();
    f.options = options.split("\n").map((o) => o.trim()).filter(Boolean)
      .map((o) => { const value = uniqueCode(o, seen); seen.add(value); return { value, label: o }; });
  }
  return f;
}

/** Inline form that adds one question to a diary group (codes are generated so results stay linked by question). */
export function DiaryAddQuestion({ group, taken, onAdd, onCancel }) {
  const types = group === "day_fields" ? TEXT_ONLY : Object.keys(TYPE_LABEL);
  const [v, setV] = useState({ type: types[0], label: "", prompt: "", help: "", options: "", unit: "", better: "lower" });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const opts = v.options.split("\n").map((o) => o.trim().toLowerCase()).filter(Boolean);
  const dupes = new Set(opts).size !== opts.length;
  const ok = v.label.trim() && v.prompt.trim() && (v.type !== "single" || (opts.length >= 2 && !dupes));
  const tid = `diary-add-${group}`;
  return (
    <div className="space-y-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-3" data-testid={`${tid}-form`}>
      <div className="grid gap-2 sm:grid-cols-3">
        <div><Label className="text-xs">Answer type</Label>
          <Select value={v.type} onValueChange={set("type")}>
            <SelectTrigger className="mt-1 h-8" data-testid={`${tid}-type`}><SelectValue /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t} value={t} data-testid={`${tid}-type-${t}`}>{TYPE_LABEL[t]}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Short label (charts & tables)</Label>
          <Input className="mt-1 h-8" value={v.label} onChange={set("label")} placeholder="e.g. Pain during" data-testid={`${tid}-label`} /></div>
        {["rating", "integer"].includes(v.type) && (
          <div><Label className="text-xs">In comparisons</Label>
            <Select value={v.better} onValueChange={set("better")}>
              <SelectTrigger className="mt-1 h-8" data-testid={`${tid}-better`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="lower">Lower is better</SelectItem><SelectItem value="higher">Higher is better</SelectItem><SelectItem value="none">Neither</SelectItem></SelectContent>
            </Select></div>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div><Label className="text-xs">Question the client sees</Label>
          <Input className="mt-1 h-8" value={v.prompt} onChange={set("prompt")} placeholder="e.g. Pain during (0–10)" data-testid={`${tid}-prompt`} /></div>
        <div><Label className="text-xs">Help text (optional)</Label>
          <Input className="mt-1 h-8" value={v.help} onChange={set("help")} placeholder="e.g. 0 = no pain · 10 = worst imaginable" data-testid={`${tid}-help`} /></div>
      </div>
      {v.type === "integer" && <div className="w-48"><Label className="text-xs">Unit (optional)</Label>
        <Input className="mt-1 h-8" value={v.unit} onChange={set("unit")} placeholder="e.g. minutes" data-testid={`${tid}-unit`} /></div>}
      {v.type === "single" && <div><Label className="text-xs">Answer options — one per line (at least 2)</Label>
        <Textarea rows={3} className="mt-1 text-sm" value={v.options} onChange={set("options")} data-testid={`${tid}-options`} />
        {dupes && <p className="mt-1 text-2xs text-red-700" data-testid={`${tid}-options-dupe`}>Each option needs different wording.</p>}</div>}
      <div className="flex gap-2">
        <Button size="sm" disabled={!ok} onClick={() => onAdd(buildField(v, taken))} data-testid={`${tid}-save`}>Add question</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`${tid}-cancel`}>Cancel</Button>
      </div>
    </div>
  );
}
